import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Canton } from '@/types/insurance';

interface CantonSelectorProps {
  selectedCanton: Canton | null;
  onCantonChange: (canton: Canton) => void;
  onResetToDefaults?: () => void;
  showResetButton?: boolean;
  className?: string;
}

const cantonOptions = [
  { value: 'ZH', label: 'Zürich (ZH)', description: 'Kanton Zürich' },
  { value: 'GL', label: 'Glarus (GL)', description: 'Kanton Glarus' }
];

export const CantonSelector: React.FC<CantonSelectorProps> = ({
  selectedCanton,
  onCantonChange,
  onResetToDefaults,
  showResetButton = true,
  className
}) => {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Kanton
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="canton-select">Steuer- und Versicherungskanton</Label>
          <Select
            value={selectedCanton || ''}
            onValueChange={(value) => onCantonChange(value as Canton)}
          >
            <SelectTrigger id="canton-select">
              <SelectValue placeholder="Kanton auswählen..." />
            </SelectTrigger>
            <SelectContent>
              {cantonOptions.map((canton) => (
                <SelectItem key={canton.value} value={canton.value}>
                  <div className="flex flex-col">
                    <span className="font-medium">{canton.label}</span>
                    <span className="text-xs text-muted-foreground">{canton.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedCanton && (
          <div className="bg-muted/50 p-3 rounded-lg">
            <p className="text-sm font-medium mb-1">
              Automatische Standardsätze für {cantonOptions.find(c => c.value === selectedCanton)?.label}
            </p>
            <p className="text-xs text-muted-foreground">
              Die Versicherungssätze werden automatisch basierend auf den kantonalen 
              Vorschriften gesetzt. Sie können diese individuell anpassen.
            </p>
          </div>
        )}

        {showResetButton && onResetToDefaults && selectedCanton && (
          <Button
            variant="outline"
            size="sm"
            onClick={onResetToDefaults}
            className="w-full"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Auf Kantons-Standard zurücksetzen
          </Button>
        )}
      </CardContent>
    </Card>
  );
};